import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';

const Login = lazy(() => import('./admin/pages/Login'));
const AdminLayout = lazy(() => import('./admin/components/AdminLayout'));
const Dashboard = lazy(() => import('./admin/pages/Dashboard'));
const Products = lazy(() => import('./admin/pages/Products'));
const ProductPricing = lazy(() => import('./admin/pages/ProductPricing'));
const Pages = lazy(() => import('./admin/pages/Pages'));
const BlogPosts = lazy(() => import('./admin/pages/BlogPosts'));
const BlogEditor = lazy(() => import('./admin/pages/BlogEditor'));
const SEOManager = lazy(() => import('./admin/pages/SEOManager'));
const SitemapGenerator = lazy(() => import('./admin/pages/SitemapGenerator'));
const MediaManager = lazy(() => import('./admin/pages/MediaManager'));
const SiteSettings = lazy(() => import('./admin/pages/SiteSettings'));
const DataScraperPage = lazy(() => import('./admin/pages/DataScraper'));
const Plugins = lazy(() => import('./admin/pages/Plugins'));
const Themes = lazy(() => import('./admin/pages/Themes'));
const Inquiries = lazy(() => import('./admin/pages/Inquiries'));
const LeadManager = lazy(() => import('./admin/pages/LeadManager'));
const IndiaMARTLeads = lazy(() => import('./admin/pages/IndiaMARTLeads'));
const Settings = lazy(() => import('./admin/pages/Settings'));
const PageEditor = lazy(() => import('./admin/pages/PageEditor'));
const ProductEditor = lazy(() => import('./admin/pages/ProductEditor'));
const WordPressImporter = lazy(() => import('./admin/pages/WordPressImporter'));
const WhatsAppMarketing = lazy(() => import('./admin/pages/WhatsAppMarketing'));
const WhatsAppCampaignEditor = lazy(() => import('./admin/pages/WhatsAppCampaignEditor'));
const WhatsAppCampaignDetails = lazy(() => import('./admin/pages/WhatsAppCampaignDetails'));
const WhatsAppSettings = lazy(() => import('./admin/pages/WhatsAppSettings'));
const WhatsAppLeads = lazy(() => import('./admin/pages/WhatsAppLeads'));
const Calculators = lazy(() => import('./admin/pages/Calculators'));

// Public website components
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import ProductsSection from './components/Products';
import WhyChooseUs from './components/WhyChooseUs';
import Testimonials from './components/Testimonials';
import Certifications from './components/Certifications';
import Contact from './components/Contact';
import Footer from './components/Footer';

// Dynamic page view
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import SleepCalculator from './pages/SleepCalculator';
import RetirementCalculator from './pages/RetirementCalculator';
import CreditCardEMICalculator from './pages/CreditCardEMICalculator';

// Styles
import './styles/tiptap.css';

const PublicWebsite = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <Hero />
      <About />
      <ProductsSection />
      <WhyChooseUs />
      <Testimonials />
      <Certifications />
      <Contact />
      <Footer />
    </div>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" />;
};

const RouteLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function App() {
  return (
    <Router>
      <AuthProvider>
        <DataProvider>
          <div className="min-h-screen">
            <Routes>
              {/* Public Website */}
              <Route path="/" element={<PublicWebsite />} />
              
              {/* Blog Routes */}
              <Route path=":slug" element={<BlogPost />} />
              <Route path="/blog" element={<BlogList />} />
              
              {/* Calculator Routes */}
              <Route path="/calculators/sleep-calculator" element={<SleepCalculator />} />
              <Route path="/calculators/retirement-planning-calculator" element={<RetirementCalculator />} />
              <Route path="/calculators/credit-card-emi-calculator" element={<CreditCardEMICalculator />} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={
                <Suspense fallback={<RouteLoader />}>
                  <Login />
                </Suspense>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <Dashboard />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/dashboard" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <Dashboard />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/products" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <Products />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/products/new" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <ProductEditor />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/products/edit/:id" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <ProductEditor />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/pricing" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <ProductPricing />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/pages" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <Pages />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/pages/new" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <PageEditor />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/pages/edit/:id" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <PageEditor />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/blog" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <BlogPosts />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/blog/new" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <BlogEditor />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/blog/edit/:id" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <BlogEditor />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/seo" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <SEOManager />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/sitemap" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <SitemapGenerator />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/media" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <MediaManager />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/site-settings" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <SiteSettings />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/data-scraper" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <DataScraperPage />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/plugins" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <Plugins />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/themes" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <Themes />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/inquiries" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <Inquiries />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/leads" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <LeadManager />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/indiamart-leads" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <IndiaMARTLeads />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <Settings />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/wordpress-importer" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <WordPressImporter />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/whatsapp" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <WhatsAppMarketing />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/whatsapp/new" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <WhatsAppCampaignEditor />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/whatsapp/campaign/:id" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <WhatsAppCampaignDetails />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/whatsapp/leads" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <WhatsAppLeads />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/whatsapp/settings" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <WhatsAppSettings />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/admin/calculators" element={
                <ProtectedRoute>
                  <Suspense fallback={<RouteLoader />}>
                    <AdminLayout>
                      <Calculators />
                    </AdminLayout>
                  </Suspense>
                </ProtectedRoute>
              } />
            </Routes>
          </div>
        </DataProvider>
      </AuthProvider>
      <Toaster position="top-right" />
    </Router>
  );
}

export default App;